import React from 'react';
import { X, Building2 } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface UrbanAreaProperties {
  id: string | number;
  name: string | null;
  urban_type: string | null;
  area_sq_miles: number | null;
}

interface Props {
  area: UrbanAreaProperties;
  onClose: () => void;
}

export const UrbanAreaDetail: React.FC<Props> = ({ area, onClose }) => {
  const { theme } = useTheme();
  const color = '#6366f1';

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Building2 size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>Urban Area</h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <div style={{
          padding: '10px 14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}30`,
        }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color, marginBottom: '6px' }}>
            {area.name || 'Urban Area'}
          </div>
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.5 }}>
            Census-defined urban footprint. Community-opposition risk for new BTM data centers scales with
            population density and political organization, not just legal zoning authority.
          </div>
        </div>

        <Section title="Area Details">
          <Field label="Name" value={area.name} />
          <Field label="Type" value={area.urban_type === 'U' ? 'Urbanized Area (50K+ pop.)' : area.urban_type === 'C' ? 'Urban Cluster (2.5K–50K pop.)' : area.urban_type} />
          {area.area_sq_miles != null && (
            <Field label="Area" value={`${Number(area.area_sq_miles).toLocaleString()} sq mi`} />
          )}
        </Section>

        <Section title="BTM Siting Implications">
          <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
            <ImpactItem
              label="Community Opposition"
              detail="Meta El Paso (1 GW, 2025) drew formal opposition from 188+ groups citing noise, water, and air-quality concerns — despite TX having no county-level zoning. Precedent: organized opposition can delay PUC/TCEQ approvals by 9–18 months."
              theme={theme}
            />
            <ImpactItem
              label="Buffer Distance"
              detail="Aim for BTM pads ≥ 3 mi from urban area boundary. Reciprocating engine noise (~85 dBA at 50 ft) and warm-side emissions are the flashpoints."
              theme={theme}
            />
            <ImpactItem
              label="TCEQ Permitting"
              detail="TCEQ air permits near urban areas receive more public comment and require EJ analysis. Emissions modeling must show no ambient increase at nearest receptor."
              theme={theme}
            />
            <ImpactItem
              label="Workforce"
              detail="Proximity helps for 24/7 staffing (O&M rotation). Trade-off between buffer and workforce access is site-specific — Crusoe Abilene solved this at 7 mi."
              theme={theme}
            />
          </div>
        </Section>

        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            US Census TIGER/Line · 2020 Urban Areas (2023 vintage delineation).
          </div>
        </Section>
      </div>
    </div>
  );
};

const ImpactItem: React.FC<{ label: string; detail: string; theme: ReturnType<typeof useTheme>['theme'] }> = ({ label, detail, theme }) => (
  <div style={{ marginBottom: '10px', paddingLeft: '10px', borderLeft: `2px solid ${theme.borderLight}` }}>
    <div style={{ fontSize: '12px', fontWeight: 600, color: theme.text, marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.5 }}>{detail}</div>
  </div>
);
